import { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import { PageHeader, Panel, StatusPill, EmptyState } from "../../components/Bits";
import { Modal } from "../admin/Students";
import { api, formatApiError } from "../../lib/api";
import { Plus } from "lucide-react";
import { toast } from "sonner";

const CATS = [
  { v: "fan", l: "Fan" },{ v: "light", l: "Light / electricity" },{ v: "cooler", l: "Cooler / AC" },
  { v: "water_cooler", l: "Water cooler" },{ v: "furniture", l: "Furniture" },
  { v: "plumbing", l: "Plumbing / water" },{ v: "other", l: "Other" }
];

const CAT_TINT = {
  fan: "bg-[#FBEED7] text-[#8C6D31]", light: "bg-[#FBEED7] text-[#8C6D31]",
  cooler: "bg-[#D6E6EB] text-[#3D5C68]", water_cooler: "bg-[#D6E6EB] text-[#3D5C68]",
  plumbing: "bg-[#D6E6EB] text-[#3D5C68]", furniture: "bg-[#EAE5DE] text-[#5A5652]",
  other: "bg-[#F4F1EA] text-[#75716E]",
};

export default function StudentTickets() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "fan", title: "", description: "", room_number: "" });
  const [saving, setSaving] = useState(false);

  async function load() { const { data } = await api.get("/tickets"); setRows(data); }
  useEffect(() => { load(); }, []);

  async function save(e) {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/tickets", form);
      toast.success("Ticket raised. Warden has been notified.");
      setOpen(false); setForm({ category: "fan", title: "", description: "", room_number: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  return (
    <AppShell>
      <PageHeader
        overline="Maintenance"
        title="My tickets"
        subtitle="Something not working? Let us know — we'll fix it quickly."
        actions={<button onClick={() => setOpen(true)} data-testid="raise-ticket-btn" className="bg-terracotta text-white px-5 py-2.5 rounded-md text-sm inline-flex items-center gap-2"><Plus className="h-4 w-4" /> Raise ticket</button>}
      />

      <Panel>
        {rows === null ? <div className="text-sm text-warm">Loading…</div>
          : rows.length === 0 ? <EmptyState title="No tickets" hint="You haven't raised any maintenance tickets yet." />
            : (
              <ul className="divide-y divide-[#EAE5DE]">
                {rows.map((t) => (
                  <li key={t.id} className="py-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs capitalize font-medium ${CAT_TINT[t.category] || CAT_TINT.other}`}>{t.category.replace("_", " ")}</span>
                      <StatusPill status={t.status} />
                      <span className="text-xs text-warm">{new Date(t.created_at).toLocaleString()}</span>
                    </div>
                    <div className="font-medium text-deep mt-2">{t.title}</div>
                    <div className="text-sm text-warm">{t.description}</div>
                    {t.admin_note && <div className="mt-2 text-sm bg-[#F4F1EA] border border-warm rounded-md px-3 py-2"><span className="text-xs uppercase tracking-wider text-[#A67B5B]">Warden note </span>{t.admin_note}</div>}
                  </li>
                ))}
              </ul>
            )}
      </Panel>

      {open && (
        <Modal title="Raise a maintenance ticket" onClose={() => setOpen(false)}>
          <form onSubmit={save} className="space-y-4">
            <FieldL label="Category *">
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={input} data-testid="ticket-category">
                {CATS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
            </FieldL>
            <FieldL label="Short title *"><input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={input} placeholder="e.g. Ceiling fan stopped working" data-testid="ticket-title" /></FieldL>
            <FieldL label="Description *"><textarea required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={input} data-testid="ticket-desc" /></FieldL>
            <FieldL label="Room number"><input value={form.room_number} onChange={(e) => setForm({ ...form, room_number: e.target.value })} className={input} placeholder="leave blank to use your room" /></FieldL>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="px-5 py-2 rounded-md text-sm border border-warm">Cancel</button>
              <button disabled={saving} type="submit" data-testid="submit-ticket-btn" className="bg-terracotta text-white px-6 py-2 rounded-md text-sm disabled:opacity-60">{saving ? "Submitting…" : "Submit"}</button>
            </div>
          </form>
        </Modal>
      )}
    </AppShell>
  );
}

const input = "w-full px-3 py-2 rounded-md border border-warm bg-white focus:outline-none focus:ring-2 focus:ring-[#E8C2B8] focus:border-[#C86F58] text-sm";
function FieldL({ label, children }) { return <label className="block"><span className="text-xs uppercase tracking-wider text-deep font-medium mb-1.5 block">{label}</span>{children}</label>; }
